import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useAuthStore } from '../../stores/authStore';
import { useDialogStore } from '../../stores/dialogStore';
import ProtectedImage from '../ProtectedImage';

const ProfileImageGallery = ({ profileId, isOwnProfile }) => {
  const { accessToken, refreshProfilePicture } = useAuthStore();
  const { confirm } = useDialogStore();
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);

  // Fetch profile gallery
  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/profile/gallery/${profileId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setGallery(data.gallery);
        setImages(data.gallery.images || []);
      } else {
        setError(data.message || 'Failed to load gallery');
      }
    } catch (err) {
      setError('Failed to load gallery');
      console.error('Gallery fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileId && accessToken) {
      fetchGallery();
    }
  }, [profileId, accessToken]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setOriginalFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  // Handle crop complete
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image blob
  const createCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return null;

    const image = new Image();
    image.src = imageSrc;

    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        canvas.toBlob((blob) => {
          resolve(blob);
        }, originalFile.type);
      };
    });
  };

  // Upload cropped image
  const handleUpload = async () => {
    try {
      setLoading(true);
      const croppedBlob = await createCroppedImage();

      if (!croppedBlob) {
        setError('Failed to process image');
        return;
      }

      const formData = new FormData();
      formData.append('image', croppedBlob, originalFile.name);

      const response = await fetch(`/api/profile/picture/${profileId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setShowCropModal(false);
        setImageSrc(null);
        setOriginalFile(null);
        await fetchGallery(); // Refresh gallery
      } else {
        setError(data.message || 'Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Set active profile picture
  const handleSetActive = async (assetId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/profile/picture/select/${profileId}/${assetId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        await fetchGallery(); // Refresh gallery
        refreshProfilePicture(); // Trigger avatar refresh across the app
      } else {
        setError(data.message || 'Failed to set active picture');
      }
    } catch (err) {
      setError('Failed to set active picture');
      console.error('Set active error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete profile picture
  const handleDelete = async (assetId) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this picture?',
      'Delete Picture',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/profile/picture/${assetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        await fetchGallery(); // Refresh gallery
      } else {
        setError(data.message || 'Failed to delete picture');
      }
    } catch (err) {
      setError('Failed to delete picture');
      console.error('Delete error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
      {/* Header with upload button */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-primary">Profile Pictures</h3>

        {/* Upload button - only for own profile */}
        {isOwnProfile && (
          <label className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 cursor-pointer transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
          {error}
        </div>
      )}

      {/* Image gallery */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <ProtectedImage
                uuid={image.uuid}
                alt={image.storage_key}
                className={`w-full aspect-square object-cover rounded ${
                  image.is_selected ? 'ring-2 ring-primary-600' : ''
                }`}
              />

              {/* Action buttons - only for own profile */}
              {isOwnProfile && (
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {!image.is_selected && (
                    <button
                      onClick={() => handleSetActive(image.id)}
                      className="p-1 bg-green-600 text-white rounded hover:bg-green-700"
                      title="Set as active"
                      disabled={loading}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                    title="Delete"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Crop modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-primary mb-4">Crop Your Profile Picture</h3>

            <div className="relative h-96 mb-4">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCropModal(false);
                  setImageSrc(null);
                  setOriginalFile(null);
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                disabled={loading}
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImageGallery;
